/**
 * Riftgate Arena — locale-aware number, money, date and list formatting.
 *
 * Every one of these is `Intl` under a cache. `Intl` is in the browser already,
 * so this costs bytes only for the wrapper — and it exists so that three screens
 * built by three people do not invent three different ways to render a price.
 *
 * ── MONEY IS MINOR UNITS ────────────────────────────────────────────────────
 * The backend's accounting is EUR cents (`core/economy`, `CurrencyLedger`), and
 * a price crossing a JS `number` as `12.34` is a rounding bug waiting for a
 * quantity multiplier. `formatCurrency` therefore takes an INTEGER in minor
 * units and does the division itself, using the currency's own exponent — 2 for
 * EUR/GBP/USD, 0 for JPY, which matters the moment a Japanese player is shown a
 * price. `formatCurrencyMajor` exists for values that are already decimal.
 */
import { DEFAULT_LOCALE, localeTag, type Locale } from './locales';
import { getLocale } from './runtime';

/** Currencies whose smallest unit IS the major unit. The rest are 2 decimals. */
const ZERO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG',
  'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

/** Minor-unit exponent. Mirrors `minorUnits()` in `core/i18n/i18n.constants.ts`. */
export function minorUnitsFor(currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase()) ? 0 : 2;
}

type FormatterKind = 'number' | 'date' | 'relative' | 'list';

const cache = new Map<string, unknown>();

/**
 * Memoise a formatter. Construction is ~100x the cost of a `format()` call and
 * these are built inside render loops.
 *
 * Deliberately does NOT swallow errors: `Intl` throws on a bad currency code or
 * an unsupported option, and each public function below decides what a sensible
 * degraded output is for ITS value. A generic catch here could only return
 * something meaningless.
 */
function cached<T>(kind: FormatterKind, tag: string, options: object, make: () => T): T {
  const key = `${kind}|${tag}|${JSON.stringify(options)}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit as T;
  const made = make();
  cache.set(key, made);
  return made;
}

function tagFor(locale?: Locale): string {
  return localeTag(locale ?? getLocale());
}

/** Tests only — formatters are keyed by tag, so this is rarely needed. */
export function clearFormatterCache(): void {
  cache.clear();
}

/* ═══════════════════════════════════════════════════════════ numbers ════ */

export function formatNumber(
  value: number,
  options: Intl.NumberFormatOptions = {},
  locale?: Locale,
): string {
  if (!Number.isFinite(value)) return String(value);
  const tag = tagFor(locale);
  try {
    return cached('number', tag, options, () => new Intl.NumberFormat(tag, options)).format(value);
  } catch {
    return String(value);
  }
}

/**
 * The character this locale writes between the units and the fraction: `.` in
 * en and ja, `,` in de, es, lv, pt-BR, ru and tr.
 *
 * Asked of `Intl` rather than kept in a table of our own, because a table is a
 * second source of truth for something the platform already knows and eight
 * locales is exactly enough for one of the eight to be wrong in it.
 *
 * It exists for ONE reader: the deposit field, which has to accept the
 * separator a European player's keyboard and muscle memory produce ("2000,00")
 * and echo the amount back in the same shape. Nothing that RENDERS money needs
 * it — `formatCurrency` is the answer there.
 */
export function decimalSeparator(locale?: Locale): string {
  const tag = tagFor(locale);
  try {
    const parts = cached('number', tag, { minimumFractionDigits: 1 }, () =>
      new Intl.NumberFormat(tag, { minimumFractionDigits: 1 }),
    ).formatToParts(1.5);
    return parts.find((part) => part.type === 'decimal')?.value ?? '.';
  } catch {
    return '.';
  }
}

/** Compact form for big counts: `12K`, `1.2M`. */
export function formatCompact(value: number, locale?: Locale): string {
  return formatNumber(value, { notation: 'compact', maximumFractionDigits: 1 }, locale);
}

/** `0.075` -> `7.5%`. Takes a FRACTION, not a percentage. */
export function formatPercent(
  fraction: number,
  options: Intl.NumberFormatOptions = {},
  locale?: Locale,
): string {
  return formatNumber(fraction, { style: 'percent', maximumFractionDigits: 1, ...options }, locale);
}

/**
 * Format an integer amount in MINOR units (cents) as money.
 *
 *     formatCurrency(499, 'EUR')   // "€4.99" in en, "4,99 €" in de
 *     formatCurrency(499, 'JPY')   // "¥499" — no division, no decimals
 */
export function formatCurrency(
  amountMinor: number,
  currency: string,
  options: Intl.NumberFormatOptions = {},
  locale?: Locale,
): string {
  const exponent = minorUnitsFor(currency);
  const major = amountMinor / 10 ** exponent;
  return formatCurrencyMajor(major, currency, options, locale);
}

/** Format an amount already expressed in major units. */
export function formatCurrencyMajor(
  amount: number,
  currency: string,
  options: Intl.NumberFormatOptions = {},
  locale?: Locale,
): string {
  const code = currency.toUpperCase();
  const exponent = minorUnitsFor(code);
  const tag = tagFor(locale);
  const opts: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: code,
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
    ...options,
  };
  try {
    return cached('number', tag, opts, () => new Intl.NumberFormat(tag, opts)).format(amount);
  } catch {
    // An unknown ISO code throws inside Intl; show something true rather than
    // nothing at all.
    return `${amount.toFixed(exponent)} ${code}`;
  }
}

/**
 * In-game soft/premium currency — a plain integer with grouping, never a
 * currency symbol. Sigils are not money and must never be formatted as if they
 * were; the shop's real-money prices go through `formatCurrency`.
 */
export function formatGameAmount(value: number, locale?: Locale): string {
  return formatNumber(Math.trunc(value), { maximumFractionDigits: 0 }, locale);
}

/* ═════════════════════════════════════════════════════════════ dates ════ */

export type DateStyle = 'short' | 'medium' | 'long';

const DATE_OPTIONS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { day: '2-digit', month: '2-digit', year: 'numeric' },
  medium: { day: 'numeric', month: 'short', year: 'numeric' },
  long: { day: 'numeric', month: 'long', year: 'numeric' },
};

function toDate(value: Date | number | string): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(
  value: Date | number | string,
  style: DateStyle = 'medium',
  locale?: Locale,
): string {
  const date = toDate(value);
  // An unparseable date renders as whatever we were given: wrong-looking data is
  // a bug report, an empty cell is a shrug.
  if (date === null) return String(value);
  const tag = tagFor(locale);
  const options = DATE_OPTIONS[style];
  try {
    return cached('date', tag, options, () => new Intl.DateTimeFormat(tag, options)).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

export function formatDateTime(
  value: Date | number | string,
  style: DateStyle = 'medium',
  locale?: Locale,
): string {
  const date = toDate(value);
  if (date === null) return String(value);
  const tag = tagFor(locale);
  const options: Intl.DateTimeFormatOptions = {
    ...DATE_OPTIONS[style],
    hour: '2-digit',
    minute: '2-digit',
  };
  try {
    return cached('date', tag, options, () => new Intl.DateTimeFormat(tag, options)).format(date);
  } catch {
    return date.toISOString().slice(0, 16).replace('T', ' ');
  }
}

/**
 * CLOCK TIME ONLY — `14:32`, `2:32 PM`, `午後2:32`. For a row whose DAY is
 * already obvious from where it sits: a chat message, a log line. Locale
 * decides the 12/24-hour clock, which is why this is `Intl` and not a
 * `padStart` on `getHours()`; `formatDateTime` is the one to reach for when the
 * day is not obvious.
 */
export function formatTime(value: Date | number | string, locale?: Locale): string {
  const date = toDate(value);
  if (date === null) return String(value);
  const tag = tagFor(locale);
  const options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  try {
    return cached('date', tag, options, () => new Intl.DateTimeFormat(tag, options)).format(date);
  } catch {
    return date.toISOString().slice(11, 16);
  }
}

/** ISO 8601 for `<time datetime=...>`; never localised, never shown raw. */
export function isoDate(value: Date | number | string): string {
  const date = toDate(value);
  return date === null ? String(value) : date.toISOString();
}

const RELATIVE_UNITS: ReadonlyArray<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
  { unit: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: 'week', ms: 7 * 24 * 60 * 60 * 1000 },
  { unit: 'day', ms: 24 * 60 * 60 * 1000 },
  { unit: 'hour', ms: 60 * 60 * 1000 },
  { unit: 'minute', ms: 60 * 1000 },
  { unit: 'second', ms: 1000 },
];

/**
 * "3 days ago" / "in 2 hours". `deltaMs` is target minus now, so past is
 * negative. `numeric: 'auto'` gives "yesterday" rather than "1 day ago" in the
 * languages that have such a word.
 */
export function formatRelativeTime(deltaMs: number, locale?: Locale): string {
  const tag = tagFor(locale);
  const options: Intl.RelativeTimeFormatOptions = { numeric: 'auto' };
  let formatter: Intl.RelativeTimeFormat;
  try {
    formatter = cached('relative', tag, options, () => new Intl.RelativeTimeFormat(tag, options));
  } catch {
    formatter = new Intl.RelativeTimeFormat(DEFAULT_LOCALE, options);
  }
  const abs = Math.abs(deltaMs);
  for (const { unit, ms } of RELATIVE_UNITS) {
    if (abs >= ms || unit === 'second') {
      return formatter.format(Math.round(deltaMs / ms), unit);
    }
  }
  return formatter.format(0, 'second');
}

/* ══════════════════════════════════════════════════════════════ lists ═══ */

/** "A, B and C" — conjunction by default, with a plain-join fallback. */
export function formatList(
  items: readonly string[],
  type: 'conjunction' | 'disjunction' = 'conjunction',
  locale?: Locale,
): string {
  const tag = tagFor(locale);
  const options: Intl.ListFormatOptions = { style: 'long', type };
  try {
    return cached('list', tag, options, () => new Intl.ListFormat(tag, options)).format(items);
  } catch {
    return items.join(', ');
  }
}
